// ============================================================================
// MODIFY SCHEDULED MODAL — Escape hatch for scheduled referrals.
//
// 4 action paths (scheduler only):
//   1. Reschedule:      SCHEDULED → SCHEDULED (update appointment, self-transition)
//   2. Cancel - Patient: SCHEDULED → CANCELLED (terminal, no care delivered)
//   3. Cancel - Clinic:  SCHEDULED → READY_TO_SCHEDULE (return for re-booking)
//   4. No-Show:         SCHEDULED → NO_SHOW (terminal, patient did not attend)
//
// Reuses CalendarPicker from BookAppointmentModal for the reschedule date/time.
// Zero-PHI: no patient identifiers rendered.
// ============================================================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { safeUserError } from '../core/safeError.js';
import {
  Calendar, Loader2, AlertCircle, X,
  RotateCcw, UserX, Building2, Clock,
} from 'lucide-react';
import {
  WORKFLOW_STATUS, WORKFLOW_PRIORITY,
  rescheduleAppointment, cancelPatientRequest, cancelClinicRequest, markAsNoShow,
  CANCEL_REASON, CANCEL_REASON_LABELS,
  getSchedulingAvailability, isDayAllowed, isTimeAllowed,
} from '../core/index.js';
import {
  CalendarPicker,
  fmtLabel, to24h, from24h,
} from './BookAppointmentModal.jsx';
import { RGStatusBadge } from '../components/rg/RGPrimitives.jsx';
import PingComposer from './PingComposer.jsx';
import { COMMUNICATION_PURPOSE } from '../core/index.js';
import { getApp } from 'firebase/app';
import {
  getFirestore, collection, query, where, getDocs,
} from 'firebase/firestore';

function getDb() { return getFirestore(getApp()); }

const PATIENT_REASONS = ['PATIENT_REQUEST', 'PATIENT_DECLINED', 'OTHER'];
const CLINIC_REASONS  = ['CLINIC_CAPACITY', 'CLINIC_PROVIDER_UNAVAIL', 'CLINIC_ADMIN_ERROR', 'OTHER'];

const ACTIONS = [
  { id: 'reschedule',     label: 'Reschedule',        icon: RotateCcw,  color: 'indigo' },
  { id: 'cancel_patient', label: 'Cancel — Patient',  icon: UserX,      color: 'red'    },
  { id: 'cancel_clinic',  label: 'Cancel — Clinic',   icon: Building2,  color: 'amber'  },
  { id: 'no_show',        label: 'No-Show',           icon: Clock,      color: 'orange' },
];

export default function ModifyScheduledModal({
  item,
  uid,
  role,
  onClose,
  onRescheduled,
  onCancelled,
  scheduledItems = [],
}) {
  const { referral, workflow } = item;
  const tenantId    = referral?.tenantId;
  const referralId  = referral?.id;
  const specialtyId = referral?.specialtyId ?? null;
  const priority    = workflow?.priority ?? WORKFLOW_PRIORITY.NORMAL;
  const outcome     = referral?.finalOutcome ?? referral?.outcome?.status ?? null;

  const currentAppt = workflow?.scheduling ?? {};

  const [action, setAction] = useState(null);

  // ── Reschedule state ──────────────────────────────────────────────────────
  const [selectedDate,   setSelectedDate]   = useState('');
  const [selectedTime,   setSelectedTime]   = useState('');
  const [consultantId,   setConsultantId]   = useState(currentAppt.consultantId ?? '');
  const [rescheduleNote, setRescheduleNote] = useState('');
  const [specialists,    setSpecialists]    = useState([]);
  const [specsLoading,   setSpecsLoading]   = useState(true);

  // ── Cancel state ──────────────────────────────────────────────────────────
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote,   setCancelNote]   = useState('');

  // ── No-Show state ─────────────────────────────────────────────────────────
  const [noShowConfirmed, setNoShowConfirmed] = useState(false);

  // ── Write state ───────────────────────────────────────────────────────────
  const [saving,    setSaving]    = useState(false);
  const [saveError, setSaveError] = useState(null);

  // ── Scheduling Availability (booking constraint for reschedule) ────────────
  const [availability,      setAvailability]      = useState(null);
  const [availabilityState, setAvailabilityState] = useState('loading'); // loading | ready | missing | error

  const loadAvailability = useCallback(async () => {
    if (!tenantId) { setAvailabilityState('error'); return; }
    setAvailabilityState('loading');
    try {
      const { exists, availability: a } = await getSchedulingAvailability(tenantId);
      if (!exists) { setAvailability(null); setAvailabilityState('missing'); }
      else { setAvailability(a); setAvailabilityState('ready'); }
    } catch {
      setAvailability(null);
      setAvailabilityState('error');
    }
  }, [tenantId]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  // The current appointment is exempt from availability (legacy bookings outside
  // current availability stay reschedulable to a valid new slot, or kept as-is).
  const currentAppointmentRef = useMemo(
    () => ({ date: currentAppt.appointmentDate ?? null, time: currentAppt.appointmentTime ?? null }),
    [currentAppt.appointmentDate, currentAppt.appointmentTime],
  );

  // Fetch specialists for reschedule consultant picker
  useEffect(() => {
    if (!tenantId) { setSpecsLoading(false); return; }
    setSpecsLoading(true);
    const usersRef = collection(getDb(), 'users');
    const constraints = [where('tenantId', '==', tenantId)];
    if (specialtyId) {
      constraints.push(where('specialtyIds', 'array-contains', specialtyId));
    }
    getDocs(query(usersRef, ...constraints))
      .then((snap) => {
        const results = [];
        snap.forEach((d) => {
          const data = d.data();
          if (data.role !== 'specialist') return;
          const name = data.displayName || data.name || data.email || d.id;
          results.push({ uid: d.id, name });
        });
        results.sort((a, b) => a.name.localeCompare(b.name));
        setSpecialists(results);
      })
      .catch(() => setSpecialists([]))
      .finally(() => setSpecsLoading(false));
  }, [tenantId, specialtyId]);

  // Taken slots for selected consultant + date
  const takenSlots = useMemo(() => {
    if (!consultantId || !selectedDate) return new Set();
    const taken = new Set();
    for (const { workflow: wf, referral: ref } of scheduledItems) {
      if (ref?.id === referralId) continue;
      if (
        wf?.scheduling?.consultantId === consultantId &&
        wf?.scheduling?.appointmentDate === selectedDate &&
        wf?.scheduling?.appointmentTime
      ) {
        taken.add(wf.scheduling.appointmentTime);
      }
    }
    return taken;
  }, [scheduledItems, consultantId, selectedDate, referralId]);

  useEffect(() => {
    if (selectedTime && takenSlots.has(to24h(selectedTime))) {
      setSelectedTime('');
    }
  }, [takenSlots, selectedTime]);

  // Reset form when switching actions
  useEffect(() => {
    setSaveError(null);
    setCancelReason('');
    setCancelNote('');
    setSelectedDate('');
    setSelectedTime('');
    setRescheduleNote('');
    setConsultantId(currentAppt.consultantId ?? '');
    setNoShowConfirmed(false);
  }, [action, currentAppt.consultantId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleReschedule = async () => {
    if (!selectedDate || !selectedTime || !consultantId) {
      setSaveError('Select a date, time, and specialist to reschedule.');
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const spec = specialists.find((s) => s.uid === consultantId);
      const newAppointment = {
        appointmentDate: selectedDate,
        appointmentTime: to24h(selectedTime),
        consultantId,
        consultantName:  spec?.name ?? consultantId,
        locationId:      currentAppt.locationId ?? null,
        schedulingNote:  rescheduleNote.trim() || null,
      };
      await rescheduleAppointment(tenantId, referralId, newAppointment, rescheduleNote.trim() || null, uid, role);
      onRescheduled(referralId, newAppointment);
      onClose();
    } catch (err) {
      setSaveError(safeUserError(err) ?? 'Failed to reschedule. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPatient = async () => {
    if (!cancelReason) { setSaveError('Select a cancellation reason.'); return; }
    setSaveError(null);
    setSaving(true);
    try {
      await cancelPatientRequest(tenantId, referralId, cancelReason, cancelNote.trim() || null, uid, role);
      onCancelled(referralId, 'CANCELLED');
      onClose();
    } catch (err) {
      setSaveError(safeUserError(err) ?? 'Failed to cancel. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelClinic = async () => {
    if (!cancelReason) { setSaveError('Select a cancellation reason.'); return; }
    setSaveError(null);
    setSaving(true);
    try {
      await cancelClinicRequest(tenantId, referralId, cancelReason, cancelNote.trim() || null, uid, role);
      onCancelled(referralId, 'READY_TO_SCHEDULE');
      onClose();
    } catch (err) {
      setSaveError(safeUserError(err) ?? 'Failed to cancel. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  const handleNoShow = async () => {
    if (!noShowConfirmed) { setSaveError('You must confirm the no-show action.'); return; }
    setSaveError(null);
    setSaving(true);
    try {
      await markAsNoShow(tenantId, referralId, uid, role);
      onCancelled(referralId, 'NO_SHOW');
      onClose();
    } catch (err) {
      setSaveError(safeUserError(err) ?? 'Failed to mark no-show. Please retry.');
    } finally {
      setSaving(false);
    }
  };

  // Grace window: no-show only available after the appointment datetime has passed
  const appointmentHasPassed = useMemo(() => {
    if (!currentAppt.appointmentDate) return false;
    const dateStr = currentAppt.appointmentDate;
    const timeStr = currentAppt.appointmentTime ?? '23:59';
    const apptTime = new Date(`${dateStr}T${timeStr}:00`);
    return Date.now() >= apptTime.getTime();
  }, [currentAppt.appointmentDate, currentAppt.appointmentTime]);

  const handleConfirm = () => {
    if (action === 'reschedule')     return handleReschedule();
    if (action === 'cancel_patient') return handleCancelPatient();
    if (action === 'cancel_clinic')  return handleCancelClinic();
    if (action === 'no_show')        return handleNoShow();
  };

  // A chosen reschedule slot is valid when availability is loaded and the slot is
  // allowed — or when it is unchanged from the current appointment (legacy exempt).
  const rescheduleSlotValid = (() => {
    if (!selectedDate || !selectedTime) return false;
    if (availabilityState !== 'ready') return false;
    const t = to24h(selectedTime);
    const sameAsCurrent = selectedDate === currentAppointmentRef.date && t === currentAppointmentRef.time;
    return sameAsCurrent || (isDayAllowed(availability, selectedDate) && isTimeAllowed(availability, t));
  })();

  const canConfirm = (() => {
    if (!action) return false;
    if (action === 'reschedule') return !!selectedDate && !!selectedTime && !!consultantId && rescheduleSlotValid;
    if (action === 'no_show')    return noShowConfirmed && appointmentHasPassed;
    return !!cancelReason;
  })();

  const confirmLabel = (() => {
    if (action === 'reschedule')     return 'Reschedule Appointment';
    if (action === 'cancel_patient') return 'Cancel — Patient Request';
    if (action === 'cancel_clinic')  return 'Cancel — Return to Queue';
    if (action === 'no_show')        return 'Confirm No-Show';
    return 'Confirm';
  })();

  const confirmColor = (() => {
    if (action === 'cancel_patient') return 'bg-red-600 hover:bg-red-500';
    if (action === 'cancel_clinic')  return 'bg-amber-600 hover:bg-amber-500';
    if (action === 'no_show')        return 'bg-orange-600 hover:bg-orange-500';
    return 'bg-indigo-600 hover:bg-indigo-500';
  })();

  const inputCls = 'w-full px-3 py-2 rounded-lg bg-slate-800/60 border border-slate-700/50 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-indigo-500/50';

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={() => !saving && onClose()}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-slate-900 border border-slate-700/60 rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">

          {/* Header */}
          <div className="flex items-start justify-between px-6 py-5 border-b border-slate-800/50 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-white">Modify Scheduled Appointment</h2>
              <p className="text-sm text-slate-400 mt-0.5">Reschedule or cancel this appointment.</p>
            </div>
            <button
              type="button"
              onClick={() => !saving && onClose()}
              className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Context strip */}
          <div className="px-6 py-3 bg-slate-800/40 border-b border-slate-800/50 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
              <RGStatusBadge type="workflow" status={WORKFLOW_STATUS.SCHEDULED} />
              {outcome && <RGStatusBadge type="outcome" status={outcome} />}
              {priority !== WORKFLOW_PRIORITY.NORMAL && (
                <RGStatusBadge type="priority" status={priority} />
              )}
            </div>
            <p className="text-sm font-medium text-white">
              {fmtLabel(referral?.specialtyId)}
              {referral?.packId && (
                <span className="text-slate-400 font-normal"> · {fmtLabel(referral.packId)}</span>
              )}
            </p>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{referralId ?? '---'}</p>
            {/* Current appointment summary */}
            {currentAppt.appointmentDate && (
              <div className="mt-2 flex items-center gap-2 text-xs text-teal-300/70">
                <Calendar className="w-3 h-3" />
                <span>
                  Current: {new Date(currentAppt.appointmentDate + 'T00:00:00').toLocaleDateString('en-US', {
                    weekday: 'short', month: 'short', day: 'numeric',
                  })}
                  {currentAppt.appointmentTime && ` at ${from24h(currentAppt.appointmentTime) ?? currentAppt.appointmentTime}`}
                  {currentAppt.consultantName && ` with ${currentAppt.consultantName}`}
                </span>
              </div>
            )}
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto">

            {/* Action selector */}
            <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                Choose Action
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {ACTIONS.map(({ id, label, icon: Icon, color }) => {
                  const isActive = action === id;
                  const noShowDisabled = id === 'no_show' && !appointmentHasPassed;
                  const colorMap = {
                    indigo: isActive ? 'bg-indigo-500/15 border-indigo-500/40 text-indigo-300' : '',
                    red:    isActive ? 'bg-red-500/15 border-red-500/40 text-red-300' : '',
                    amber:  isActive ? 'bg-amber-500/15 border-amber-500/40 text-amber-300' : '',
                    orange: isActive ? 'bg-orange-500/15 border-orange-500/40 text-orange-300' : '',
                  };
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => !noShowDisabled && setAction(id)}
                      disabled={saving || noShowDisabled}
                      className={[
                        'flex flex-col items-center gap-2 px-3 py-3 rounded-xl border text-xs font-medium transition-all',
                        isActive
                          ? colorMap[color]
                          : 'bg-slate-800/30 border-slate-700/40 text-slate-400 hover:border-slate-600/60 hover:text-slate-300',
                      ].join(' ')}
                    >
                      <Icon className="w-5 h-5" />
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Reschedule form ─────────────────────────────────────────────── */}
            {action === 'reschedule' && (
              <>
                {/* Specialist */}
                <div className="px-6 pt-5 pb-4 border-b border-slate-800/50">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Specialist
                  </label>
                  {specsLoading ? (
                    <div className={`${inputCls} flex items-center gap-2 text-slate-500`}>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <select value={consultantId} onChange={(e) => setConsultantId(e.target.value)} className={inputCls}>
                      <option value="">--- Select specialist ---</option>
                      {specialists.map(({ uid: sUid, name }) => (
                        <option key={sUid} value={sUid}>{name}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Calendar + time */}
                <div className="px-6 pt-5 pb-4">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-4">
                    New Date &amp; Time
                  </p>

                  {availabilityState === 'loading' && (
                    <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading scheduling availability…
                    </div>
                  )}

                  {availabilityState === 'missing' && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                      <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-300">
                        Scheduling availability has not been set up. A tenant admin must configure it in Impact Settings → Scheduling Availability before rescheduling.
                      </p>
                    </div>
                  )}

                  {availabilityState === 'error' && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                      <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                      <div className="text-xs text-red-300">
                        Could not load scheduling availability. Rescheduling is blocked until it loads.{' '}
                        <button type="button" onClick={loadAvailability} className="underline underline-offset-2 hover:text-red-200">
                          Retry
                        </button>
                      </div>
                    </div>
                  )}

                  {availabilityState === 'ready' && (
                    <CalendarPicker
                      selectedDate={selectedDate}
                      onSelectDate={setSelectedDate}
                      selectedTime={selectedTime}
                      onSelectTime={setSelectedTime}
                      takenSlots={takenSlots}
                      availability={availability}
                      currentAppointment={currentAppointmentRef}
                    />
                  )}
                </div>

                {/* Selected summary pill */}
                {selectedDate && (
                  <div className="px-6 pb-4">
                    <div className={[
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium',
                      selectedTime
                        ? 'bg-indigo-500/15 border-indigo-500/30 text-indigo-300'
                        : 'bg-slate-700/40 border-slate-600/30 text-slate-400',
                    ].join(' ')}>
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                      })}
                      {selectedTime && (
                        <><span className="text-slate-500 mx-0.5">·</span>{selectedTime}</>
                      )}
                    </div>
                  </div>
                )}

                <div className="border-t border-slate-800/50 mx-6" />

                {/* Reschedule note */}
                <div className="px-6 py-4">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                    Reschedule Note{' '}
                    <span className="normal-case font-normal text-slate-600">(optional · non-PHI)</span>
                  </label>
                  <input
                    type="text"
                    value={rescheduleNote}
                    onChange={(e) => setRescheduleNote(e.target.value)}
                    placeholder="Reason for reschedule..."
                    className={inputCls}
                  />
                </div>
              </>
            )}

            {/* ── Cancel — Patient form ──────────────────────────────────────── */}
            {action === 'cancel_patient' && (
              <div className="px-6 pt-5 pb-4">
                <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/15 mb-4">
                  <p className="text-xs text-red-300/80">
                    This will permanently cancel the appointment. The referral will be marked as <strong>Cancelled</strong> and cannot be re-scheduled.
                  </p>
                </div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Cancellation Reason
                </label>
                <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className={inputCls}>
                  <option value="">--- Select reason ---</option>
                  {PATIENT_REASONS.map((key) => (
                    <option key={key} value={key}>{CANCEL_REASON_LABELS[key]}</option>
                  ))}
                </select>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-4">
                  Note{' '}
                  <span className="normal-case font-normal text-slate-600">(optional · non-PHI)</span>
                </label>
                <input
                  type="text"
                  value={cancelNote}
                  onChange={(e) => setCancelNote(e.target.value)}
                  placeholder="Additional details..."
                  className={inputCls}
                />
              </div>
            )}

            {/* ── Cancel — Clinic form ───────────────────────────────────────── */}
            {action === 'cancel_clinic' && (
              <div className="px-6 pt-5 pb-4">
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/15 mb-4">
                  <p className="text-xs text-amber-300/80">
                    This will return the referral to <strong>Ready to Schedule</strong> so it can be re-booked with a different date, time, or specialist.
                  </p>
                </div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                  Cancellation Reason
                </label>
                <select value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} className={inputCls}>
                  <option value="">--- Select reason ---</option>
                  {CLINIC_REASONS.map((key) => (
                    <option key={key} value={key}>{CANCEL_REASON_LABELS[key]}</option>
                  ))}
                </select>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-4">
                  Note{' '}
                  <span className="normal-case font-normal text-slate-600">(optional · non-PHI)</span>
                </label>
                <input
                  type="text"
                  value={cancelNote}
                  onChange={(e) => setCancelNote(e.target.value)}
                  placeholder="Additional details..."
                  className={inputCls}
                />
              </div>
            )}

            {/* ── No-Show form ──────────────────────────────────────────────── */}
            {action === 'no_show' && (
              <div className="px-6 pt-5 pb-4">
                <div className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/15 mb-4">
                  <p className="text-xs text-orange-300/80">
                    This will permanently mark the referral as <strong>No-Show</strong>. The patient did not attend the scheduled appointment. This action is terminal and cannot be undone.
                  </p>
                </div>
                {currentAppt.appointmentDate && (
                  <div className="flex items-center gap-2 text-xs text-slate-400 mb-4">
                    <Calendar className="w-3 h-3" />
                    <span>
                      Appointment was {new Date(currentAppt.appointmentDate + 'T00:00:00').toLocaleDateString('en-US', {
                        weekday: 'short', month: 'short', day: 'numeric',
                      })}
                      {currentAppt.appointmentTime && ` at ${from24h(currentAppt.appointmentTime) ?? currentAppt.appointmentTime}`}
                    </span>
                  </div>
                )}
                <label className="flex items-start gap-2.5 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={noShowConfirmed}
                    onChange={(e) => setNoShowConfirmed(e.target.checked)}
                    className="mt-0.5 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/30"
                  />
                  <span className="text-xs text-slate-300 group-hover:text-white transition-colors">
                    I confirm that the appointment time has passed and the patient did not attend.
                  </span>
                </label>
              </div>
            )}

            {/* Error */}
            {saveError && (
              <div className="mx-6 mb-4 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-300">{saveError}</p>
              </div>
            )}

            {/* Referral Ping — ask the assigned specialist to review this appointment */}
            {workflow && (
              <div className="px-6 pb-4 border-t border-slate-800/50 pt-4">
                <PingComposer
                  tenantId={tenantId}
                  referralId={referralId}
                  workflow={workflow}
                  uid={uid}
                  role={role}
                  defaultPurpose={COMMUNICATION_PURPOSE.SCHEDULED_APPOINTMENT_REVIEW}
                  defaultReceiverUid={currentAppt.consultantId || undefined}
                  receiverFilterContext={specialtyId ? { specialtyId, requireSpecialtyMatch: true } : undefined}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-800/50 flex-shrink-0">
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => !saving && onClose()}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl border border-slate-700/50 text-slate-400 hover:text-white hover:border-slate-600 text-sm font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={saving || !canConfirm}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl ${confirmColor} disabled:opacity-40 text-white text-sm font-medium transition-colors`}
              >
                {saving
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>
                  : confirmLabel}
              </button>
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
